import { MelodyPlayerListener } from "../audio/MelodyPlayer.js";
import { VOMelodyInstrument, VOMelodyInstrumentTone, VOMelodyNote } from "../model/DataStructure.js";

export interface VisualInstrumentOptions {
    container: HTMLElement;
    instrument?: VOMelodyInstrument;
    visible?: boolean;
    clickable?: boolean;
    clickArea?: HTMLElement;
    showDashes: boolean;
}

export class VisualInstrument implements MelodyPlayerListener {

    public readonly onAttack = e5.Signal.create<[VisualInstrumentArea]>();

    public readonly correct: VisualInstrumentInfo;
    public readonly wrong: VisualInstrumentInfo;

    private static _svgLookup: { [instrumentId: string]: VisualInstrumentSvg } = {};

    private _pointerInfos: {
        [id: string]: {
            area: VisualInstrumentArea | null,
        }
    } = {};

    private _areaInfos: {
        [note: string]: {
            pointerCount: number;
        }
    } = {};
    private _options: VisualInstrumentOptions;
    private _element: HTMLDivElement;
    private _svg: VisualInstrumentSvg | null = null;
    private _instrument: VOMelodyInstrument = null;

    constructor(options: VisualInstrumentOptions) {
        this._options = e5.object.merge({
            visible: false,
            clickable: true,
            showDashes: false
        }, options);


        this._element = document.createElement("div");
        this._element.className = "visual-instrument";

        if (!this._options.visible) {
            this._element.classList.add("visual-instrument--hidden");
        }

        this.wrong = new VisualInstrumentInfo({
            icon: "e5 icon-592",
            container: this._element,
            className: "visual-instrument__area-info--wrong",
            instrument: this
        });

        this.correct = new VisualInstrumentInfo({
            icon: "e5 icon-590",
            container: this._element,
            className: "visual-instrument__area-info--correct",
            instrument: this
        });

        if (options.instrument) {
            this.instrument = options.instrument;
        }

        this.clickable = this._options.clickable;

        if (options.container) {
            options.container.appendChild(this._element);
        }

        const attack = (area: VisualInstrumentArea) => {
            // this.triggerArea(area);

            area.areaHover.style.opacity = "1";
            this._instrument.virtual.attack(area.note);
            this.onAttack.dispatch(area);
        };

        const release = (area: VisualInstrumentArea) => {
            area.areaHover.style.opacity = null;
            this._instrument.virtual.release(area.note);
        };

        const isEnabled = (): boolean => {
            if (!this.visible
                || !this.clickable
                || !this._svg) {

                const keys = Object.keys(this._pointerInfos);
                if (keys && keys.length > 0) {
                    for (let key of keys) {
                        if (this._pointerInfos[key].area) {
                            release(this._pointerInfos[key].area);
                        }
                    }
                }

                this._pointerInfos = {};
                this._areaInfos = {};
                return false;
            }
            return true;
        };

        let scope = this._options.clickArea || this._element;

        scope.addEventListener("pointerdown", (e) => {
            if (!isEnabled()) {
                return;
            }

            this._pointerInfos[e.pointerId] = {
                area: this._svg.hitTest(e.clientX, e.clientY)
            };

            if (this._pointerInfos[e.pointerId].area) {
                if (this._areaInfos[this._pointerInfos[e.pointerId].area.note]) {
                    this._areaInfos[this._pointerInfos[e.pointerId].area.note].pointerCount++;
                } else {
                    this._areaInfos[this._pointerInfos[e.pointerId].area.note] = {
                        pointerCount: 1
                    };
                }
                attack(this._pointerInfos[e.pointerId].area);
            }

        }, { capture: true, passive: true });

        scope.addEventListener("pointermove", (e) => {
            if (!isEnabled()) {
                return;
            }

            //Pointer is not down yet...
            if (!this._pointerInfos[e.pointerId]) {
                return;
            }
            const area = this._svg.hitTest(e.clientX, e.clientY);

            //Nothing changed
            if (this._pointerInfos[e.pointerId]?.area === area) {
                return;
            }

            //Pointer left another area
            if (this._pointerInfos[e.pointerId].area) {
                let pointerCount = this._areaInfos[this._pointerInfos[e.pointerId].area.note].pointerCount;
                pointerCount--;
                if (pointerCount <= 0) {
                    release(this._pointerInfos[e.pointerId].area);
                    delete this._areaInfos[this._pointerInfos[e.pointerId].area.note];
                } else {
                    this._areaInfos[this._pointerInfos[e.pointerId].area.note].pointerCount = pointerCount;
                }
            }

            this._pointerInfos[e.pointerId].area = area;

            //Pointer hits a new area
            if (this._pointerInfos[e.pointerId].area) {

                if (this._areaInfos[this._pointerInfos[e.pointerId].area.note]) {
                    this._areaInfos[this._pointerInfos[e.pointerId].area.note].pointerCount++;
                } else {
                    this._areaInfos[this._pointerInfos[e.pointerId].area.note] = {
                        pointerCount: 1
                    };
                }

                attack(this._pointerInfos[e.pointerId].area);
            }
        }, { capture: true, passive: true });


        const onUp = (e: PointerEvent) => {
            if (!this._pointerInfos[e.pointerId]) {
                return;
            }

            if (!isEnabled()) {
                return;
            }

            if (this._pointerInfos[e.pointerId]?.area) {
                let pointerCount = this._areaInfos[this._pointerInfos[e.pointerId].area.note].pointerCount;
                pointerCount--;
                if (pointerCount <= 0) {
                    release(this._pointerInfos[e.pointerId].area);
                    delete this._areaInfos[this._pointerInfos[e.pointerId].area.note];
                } else {
                    this._areaInfos[this._pointerInfos[e.pointerId].area.note].pointerCount = pointerCount;
                }
            }

            delete this._pointerInfos[e.pointerId];
        };

        document.addEventListener("pointercancel", onUp, { capture: true, passive: true });
        document.addEventListener("pointerup", onUp, { capture: true, passive: true });
    }

    public get svg() {
        return this._svg;
    }

    public get instrument(): VOMelodyInstrument {
        return this._instrument;
    }

    public set instrument(value: VOMelodyInstrument) {
        if (this._instrument?.id === value?.id) {
            return;
        }
        this._instrument = value;

        if (this._svg) {
            this._svg.remove();
        }
        this._svg = null;

        if (value) {
            if (!VisualInstrument._svgLookup[value.id]) {
                VisualInstrument._svgLookup[value.id] = new VisualInstrumentSvg({
                    instrument: value
                });
            }
            this._svg = VisualInstrument._svgLookup[value.id];
            this._svg.addTo(this._element);
        }
    }

    private triggerArea(area: VisualInstrumentArea) {
        this._instrument.virtual.trigger({
            dur: 1,
            name: area.note,
            time: 0,
            index: 0,
            midi: area.midi,
            velocity: 0.6
        }, []);

        this.animate(area);

        this.onAttack.dispatch(area);
    }

    public trigger(note: VOMelodyNote, notes: VOMelodyNote[], time: number): void {

        // console.log("NOTE", note.name);

        const area = this._svg.getArea(note);
        if (area && area.areaHover) {
            this.animate(area, note.dur);
        } else {
            console.warn("Could not play note on visual instrument. Missing area:", this._instrument.title);
        }
    }

    private animate(area: VisualInstrumentArea, duration: number = 1): void {
        area.animation.seek(0);
        area.animation.timeScale(Math.max(duration, 1.5));
        area.animation.play();
    }

    public match(note: VOMelodyNote, area: VisualInstrumentArea): boolean {
        return this._svg.getArea(note) === area;
    }

    public get clickable(): boolean {
        return this._element.classList.contains("visual-instrument--clickable");
    }

    public set clickable(value: boolean) {
        this._element.classList.toggle("visual-instrument--clickable", value);
        if (this._options.showDashes) {
            this._element.classList.toggle("visual-instrument--dashed", !value);
        }
    }

    public get visible(): boolean {
        return !this._element.classList.contains("visual-instrument--hidden");
    }

    public set visible(value: boolean) {
        this._element.classList.toggle("visual-instrument--hidden", !value);
    }
}

interface VisualInstrumentSvgOptions {
    instrument: VOMelodyInstrument;
}

interface VisualInstrumentAreaLookup {
    [note: string]: VisualInstrumentArea;
}

interface VisualInstrumentArea {
    note: string;
    midi: number;
    areaDefault: HTMLElement;
    areaHover: HTMLElement;
    animation: gsap.core.Timeline;
}

interface VisualInstrumentInfoOptions {
    container: HTMLElement;
    instrument: VisualInstrument;
    icon: string;
    className: string;
}

class VisualInstrumentInfo {

    private _element: HTMLElement;
    private _animation: gsap.core.Timeline;
    private _instrument: VisualInstrument;

    constructor(options: VisualInstrumentInfoOptions) {

        let e: HTMLElement = document.createElement("div");
        e.className = "visual-instrument__area-info " + options.className;
        e.innerHTML = `<span class="visual-instrument__area-info-icon ${options.icon}"></span>`;
        e.style.opacity = "0";
        options.container.appendChild(e);
        this._element = e;

        this._instrument = options.instrument;

        let t = gsap.timeline();
        t.fromTo(e,
            {
                opacity: 0
            },
            {
                duration: 0.25,
                opacity: 1
            },
            ">");
        t.to(e,
            {
                duration: 0.25,
                opacity: 0,
                delay: 3
            },
            ">");
        t.pause();
        this._animation = t;
    }

    public show(note: { name: string; midi?: number }) {
        if (!this._instrument.svg) {
            return;
        }

        const area = this._instrument.svg.getArea(note);
        if (!area) {
            return;
        }

        const bounds = area.areaDefault.getBoundingClientRect();
        const boundsParent = this._element.offsetParent.getBoundingClientRect();

        let scale = (this._element.offsetParent as HTMLElement).offsetWidth / boundsParent.width;

        const e = this._element;
        e.style.top = ((bounds.top - boundsParent.top) * scale) + "px";
        e.style.left = ((bounds.left - boundsParent.left) * scale) + "px";
        e.style.width = (bounds.width * scale) + "px";
        e.style.height = (bounds.height * scale) + "px";
        e.style.lineHeight = (bounds.height * scale) + "px";

        this._animation.restart();
    }
}

class VisualInstrumentSvg {
    private _element: HTMLElement;
    private _svg: SVGElement = null;
    private _areas: VisualInstrumentAreaLookup = {};
    private _options: VisualInstrumentSvgOptions;

    constructor(options: VisualInstrumentSvgOptions) {
        options = e5.object.merge({

        }, options);
        this._options = options;

        this._element = document.createElement("div");

        this.init();
    }

    public async init() {

        this._svg = await e5.load("svg", this._options.instrument.visual.url);
        this._element.appendChild(this._svg);

        for (let t of this._options.instrument.tones) {

            if (!t.area_id
                || !t.area_hover_id) {
                continue;
            }

            let area: VisualInstrumentArea = {
                note: t.note,
                midi: t.midi,
                areaDefault: this._svg.querySelector("#" + t.area_id) as HTMLElement,
                areaHover: this._svg.querySelector("#" + t.area_hover_id) as HTMLElement,
                animation: null
            };
            if (area.areaDefault) {
                area.areaDefault.classList.add("visual-instrument__area-default");
                area.areaDefault.setAttribute("data-note", area.note);

            }
            else {
                console.warn(`Missing area default "${t.area_id}" for instrument ${this._options.instrument.title}`);
            }

            if (area.areaHover) {
                area.areaHover.classList.add("visual-instrument__area-hover");
                area.animation = gsap.timeline();
                area.animation.to(area.areaHover, { opacity: 1, duration: 0.2 });
                area.animation.to(area.areaHover, { opacity: 0, duration: 0.8 });
                area.animation.pause();
            }
            else {
                console.warn(`Missing area hover "${t.area_hover_id}" for instrument ${this._options.instrument.title}`);
            }

            this._areas[t.note] = area;
        }
    }

    public getArea(note: { name: string; midi?: number }): VisualInstrumentArea | null {

        if (!this._svg) {
            return null;
        }

        if (this._areas[note.name]?.areaDefault
            && this._areas[note.name]?.areaHover) {
            return this._areas[note.name];
        }

        let finalTone: VOMelodyInstrumentTone = null;
        let dist = Number.MAX_VALUE;
        for (let t of this._options.instrument.tones) {

            if (this._areas[t.note]?.areaDefault
                && this._areas[t.note]?.areaHover) {

                let cDist = this.tonalDistance(note.midi, t.midi);
                if (cDist < dist) {
                    finalTone = t;
                    dist = cDist;
                }
            }
        }

        if (finalTone && this._areas[finalTone.note]) {
            return this._areas[finalTone.note];
        }

        return null;
    }

    public hitTest(x: number, y: number): VisualInstrumentArea | null {
        let p = document.elementFromPoint(x, y) as HTMLElement;
        while (p) {
            if (p.matches(".visual-instrument__area-default")) {
                return this._areas[p.getAttribute("data-note")];
            }
            p = p.parentElement;
        }
        return null;
    }

    private tonalDistance(midiA: number, midiB: number): number {
        return Math.abs(midiA - midiB);
    }

    public remove(): void {
        this._element.remove();
    }

    public addTo(e: HTMLElement): void {
        e.appendChild(this._element);
    }
}